import { useState } from 'react';
import { Play, Star, MessageSquare, Video, Quote, CheckCircle } from 'lucide-react';

const Testimonials = () => {
  const [activeVideo, setActiveVideo] = useState(0);

  const videoReviews = [
    {
      id: 0,
      ownerName: "Rahman Chowdhury",
      role: "Founder, Rice & Spice Chains (5 Branches)",
      quote: "Soko saved us over Tk. 80,000 in ingredient waste during our very first month.",
      thumbnail: "https://images.unsplash.com/photo-1577219491135-ce391730fb2c?auto=format&fit=crop&q=80&w=600",
      duration: "1:42"
    },
    {
      id: 1,
      ownerName: "Nadia Amin",
      role: "Operations Director, The Creamery Lab",
      quote: "The wooden table QR showpieces match our decor beautifully, and customers love ordering directly.",
      thumbnail: "https://images.unsplash.com/photo-1581092921461-eab62e97a780?auto=format&fit=crop&q=80&w=600",
      duration: "2:15"
    }
  ];

  const textReviews = [
    {
      id: 1,
      name: "Asif Zaman",
      restaurant: "Chill & Grill Bistro",
      rating: 5,
      comment: "Managing floor attendance was a nightmare before SOKO. Now my staff just clocks in at the screen terminal with a quick pin number, and I can see who is late right from my phone."
    },
    {
      id: 2,
      name: "Farhana K.",
      restaurant: "Gourmet Street Outlets",
      rating: 5,
      comment: "The real-time POS data sharing is flawless. I can track Friday night sales across our Banani and Dhanmondi branches simultaneously without having to ring up my managers every hour."
    },
    {
      id: 3,
      name: "Ehsanul Haque",
      restaurant: "The Baker's Table",
      rating: 5,
      comment: "We used to lose track of recipe margins due to rising milk and flour costs. SOKO warns us the second ingredient usage doesn't match our actual daily sales report."
    }
  ];

  return (
    <section className="py-24 bg-zinc-950 text-white relative border-t border-zinc-900" id="feedback">
      
      {/* Dynamic Background Light Fields */}
      <div className="absolute top-1/2 left-[-10%] w-[50%] h-[50%] bg-blue-600/5 rounded-full blur-[140px] pointer-events-none"></div>

      <div className="w-full px-6 sm:px-12 lg:px-16 mx-auto">
        
        {/* --- SECTION HEADER --- */}
        <div className="max-w-3xl mb-20 space-y-4">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500 font-mono block">
            // Real Operator Stories
          </span>
          <h2 className="text-3xl sm:text-5xl font-black uppercase tracking-tight">
            Trusted by the people <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-zinc-400 to-zinc-600">
              who feed our cities
            </span>
          </h2>
          <p className="text-zinc-400 text-base font-medium max-w-xl">
            See how restaurant owners and franchise operations directors are eliminating daily chaos using getsoko.app.
          </p>
        </div>

        {/* --- FEEDBACK MATRIX SPLIT --- */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
          
          {/* LEFT SIDE: Video Testimonial Console (40% Width) */}
          <div className="lg:col-span-5 flex flex-col justify-between gap-6">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-1.5 text-xs text-cyan-400 font-mono uppercase font-bold tracking-wider mb-2">
                <Video className="w-4 h-4" />
                <span>Video Case Studies</span>
              </div>
              
              {/* Main Active Video Player Placeholder */}
              <div className="relative aspect-video rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900 group shadow-2xl">
                <img 
                  src={videoReviews[activeVideo].thumbnail} 
                  alt={videoReviews[activeVideo].ownerName}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-102 brightness-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent"></div>
                
                {/* Play Button Interface */}
                <button className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-blue-500 hover:bg-blue-400 text-white rounded-full flex items-center justify-center shadow-xl transition-transform group-hover:scale-110 active:scale-95">
                  <Play className="w-6 h-6 fill-current text-zinc-950 ml-1" />
                </button>

                <span className="absolute bottom-4 right-4 bg-zinc-950/80 border border-zinc-800 text-xs px-2.5 py-1 rounded-md font-mono font-bold">
                  {videoReviews[activeVideo].duration}
                </span>
              </div>
            </div>

            {/* Video Selector Row Cards */}
            <div className="space-y-3">
              {videoReviews.map((video) => (
                <button
                  key={video.id}
                  onClick={() => setActiveVideo(video.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-all duration-300 flex items-center gap-4 ${
                    activeVideo === video.id
                    ? 'bg-zinc-900 border-blue-500 text-white shadow-xl'
                    : 'bg-zinc-900/20 border-zinc-800/60 text-zinc-400 hover:border-zinc-800 hover:bg-zinc-900/60'
                  }`}
                >
                  <div className="w-16 h-10 rounded-lg overflow-hidden shrink-0 border border-zinc-800 bg-zinc-950">
                    <img src={video.thumbnail} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate">{video.ownerName}</p>
                    <p className="text-xs text-zinc-500 truncate mt-0.5">{video.role}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* RIGHT SIDE: Written Review Feed Wall (70% Width) */}
          <div className="lg:col-span-7 bg-zinc-900/40 border border-zinc-800/80 rounded-3xl p-6 sm:p-8 flex flex-col justify-between gap-6 relative overflow-hidden">
            
            <div className="inline-flex items-center gap-1.5 text-xs text-blue-400 font-mono uppercase font-bold tracking-wider">
              <MessageSquare className="w-4 h-4" />
              <span>Verified Desk Reviews</span>
            </div>

            {/* Reviews Container Stack */}
            <div className="space-y-4 max-h-[460px] overflow-y-auto pr-2 custom-scrollbar">
              {textReviews.map((review) => (
                <div key={review.id} className="bg-zinc-950 border border-zinc-800/60 rounded-xl p-5 space-y-3 relative">
                  <Quote className="absolute top-4 right-4 w-8 h-8 text-zinc-900/60 pointer-events-none" />
                  
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-sm font-bold text-white tracking-wide">{review.name}</h4>
                      <p className="text-xs text-zinc-500 font-medium mt-0.5 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-blue-500" /> {review.restaurant}
                      </p>
                    </div>
                    <div className="flex gap-0.5 text-cyan-400">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-current" />
                      ))}
                    </div>
                  </div>

                  <p className="text-zinc-400 text-xs sm:text-sm leading-relaxed font-medium font-sans">
                    "{review.comment}"
                  </p>
                </div>
              ))}
            </div>

            {/* Bottom decoration indicator */}
            <div className="border-t border-zinc-800/80 pt-4 text-center sm:text-left">
              <p className="text-xs text-zinc-500 font-mono">
                All feedback loops authenticated via getsoko.app operations desk.
              </p>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};

export default Testimonials;